// icon-registry.ts
// Lucide-based icon registry. NavItem.icon string names must be present here.
// Add new entries when a consumer's nav references an icon that isn't yet
// registered (the dev-mode warning in resolveIcon will point you here).

import type { ComponentType, CSSProperties } from 'react';
import {
  AreaChart,
  BookOpen,
  Bot,
  Box,
  Cable,
  Code,
  Database,
  DollarSign,
  Eye,
  Home,
  LineChart,
  Network,
  Radar,
  Scan,
  ScanLine,
  Server,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Terminal,
  TrendingUp,
  User,
  Waypoints,
  Zap,
} from 'lucide-react';

export const ICON_REGISTRY = {
  AreaChart,
  BookOpen,
  Bot,
  Box,
  Cable,
  Code,
  Database,
  DollarSign,
  Eye,
  Home,
  LineChart,
  Network,
  Radar,
  Scan,
  ScanLine,
  Server,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Terminal,
  TrendingUp,
  User,
  Waypoints,
  Zap,
} as const;

export type RegisteredIconName = keyof typeof ICON_REGISTRY;

/** Lucide-icon prop shape we render with. */
type LucideIconProps = {
  size?: number | string;
  color?: string;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
};

/**
 * Look up a Lucide icon component by name. Returns `null` if not registered.
 * In development mode, emits a console.warn so missing icons surface early.
 */
export function resolveIcon(name: string | undefined): ComponentType<LucideIconProps> | null {
  if (!name) return null;
  const Icon = (ICON_REGISTRY as Record<string, ComponentType<LucideIconProps>>)[name];
  if (!Icon) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[corenet/AppShell] Icon "${name}" not registered in ICON_REGISTRY.`);
    }
    return null;
  }
  return Icon;
}
